import { useEffect, useState } from "react";
import { Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, openStripe } from "@/lib/api";

export const PlanPicker = ({ ctaLabel = "Subscribe and turn on alerts" }) => {
  const [plans, setPlans] = useState([]);
  const [selected, setSelected] = useState(null);
  const [busy, setBusy] = useState(false);
  const [checkoutUrl, setCheckoutUrl] = useState(null);

  useEffect(() => {
    api.get("/billing/plans")
      .then((p) => { setPlans(p); if (p.length) setSelected(p[0].lookup_key); })
      .catch((e) => toast.error(errMsg(e)));
  }, []);

  const checkout = async () => {
    setBusy(true);
    try {
      const r = await api.post("/billing/checkout", {
        lookup_key: selected,
        origin_url: window.location.origin,
      });
      setCheckoutUrl(r.checkout_url);
      if (!openStripe(r.checkout_url)) {
        toast.error("Your browser blocked the payment window. Use the link below.");
      }
      setBusy(false);
    } catch (e) { toast.error(errMsg(e)); setBusy(false); }
  };

  if (!plans.length) {
    return <p className="text-sm text-slate-500" data-testid="plans-loading">Loading plans...</p>;
  }

  return (
    <div className="space-y-4" data-testid="plan-picker">
      <div className="grid gap-3 sm:grid-cols-2">
        {plans.map((p) => (
          <button
            key={p.lookup_key}
            type="button"
            data-testid={`plan-${p.lookup_key}`}
            onClick={() => setSelected(p.lookup_key)}
            className={`rounded-2xl border p-5 text-left transition-all duration-200 ${
              selected === p.lookup_key ? "border-slate-900 ring-2 ring-slate-900" : "border-slate-200 hover:border-slate-400 hover:bg-slate-50"
            }`}
          >
            <div className="flex items-center justify-between">
              <span className="font-semibold text-slate-900">{p.label}</span>
              {selected === p.lookup_key && <Check className="h-4 w-4 text-slate-900" />}
            </div>
            <p className="mt-2 text-2xl font-bold text-slate-900">
              A${p.amount.toFixed(2)}
              <span className="text-sm font-normal text-slate-500"> / {p.interval}</span>
            </p>
            {p.lookup_key === "alerts_yearly" && (
              <p className="mt-0.5 text-xs font-medium text-emerald-700" data-testid="yearly-save-note">
                A$3.33/mo - save 33% vs monthly
              </p>
            )}
            {p.lookup_key === "alerts_monthly" && (
              <p className="mt-0.5 text-xs text-slate-500">About 16 cents a day</p>
            )}
            <p className="mt-1.5 text-xs font-medium text-slate-700">{p.blurb}</p>
          </button>
        ))}
      </div>
      <button
        data-testid="checkout-btn"
        disabled={busy || !selected}
        onClick={checkout}
        className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-red-700 px-7 py-3.5 font-semibold text-white transition-colors hover:bg-red-800 disabled:opacity-40"
      >
        {busy && <Loader2 className="h-4 w-4 animate-spin" />}
        {busy ? "Opening secure checkout..." : ctaLabel}
      </button>
      {checkoutUrl && (
        <a
          href={checkoutUrl}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="checkout-fallback-link"
          className="block text-sm font-medium text-red-700 underline"
        >
          Payment page didn't open? Open secure checkout in a new tab
        </a>
      )}
      <p className="text-xs text-slate-500" data-testid="promo-code-note">
        Have a discount code? Enter it on the Stripe payment page.
      </p>
      <p className="text-xs text-slate-500">
        Secure payment handled by Stripe. Cancel any time from your settings. GST-inclusive - the
        price shown is the total you pay.
      </p>
    </div>
  );
};
